import ApplicationController from './application_controller'
import { enter, leave } from './helpers/transitions'

/**
 * Dialog Controller - Manages modal dialog behavior with transitions
 *
 * Handles the display, hiding, and animation of modal dialogs using Turbo Frames.
 * Supports automatic focus management and body overflow control for modals.
 *
 * @example HTML Structure
 * <div data-controller="dialog"
 *      data-dialog-element-id-value="modal"
 *      data-dialog-autofocus-value="true"
 *      data-dialog-overflow-value="true"
 *      data-action="turbo:submit-end->dialog#hideOnSubmit
 *                   keydown.esc->dialog#hide
 *                   turbo:before-cache@window->dialog#hide">
 *   <!-- Modal content -->
 * </div>
 *
 * @example Turbo Frame
 * <turbo-frame id="modal">
 *   <!-- Modal will be loaded here -->
 * </turbo-frame>
 *
 * @example With ModalComponent
 * <%= render ModalComponent.new do |modal| %>
 *   <% modal.with_header { "Dialog Title" } %>
 *   <!-- Dialog content -->
 * <% end %>
 *
 * @value {String} elementId - ID of the Turbo Frame that loaded this dialog
 * @value {Boolean} autofocus - Whether to auto-focus the dialog on show (default: true)
 * @value {Boolean} overflow - Whether to add overflow-hidden to body (default: false)
 */
export default class extends ApplicationController {
  static values = {
    elementId: String,
    autofocus: { type: Boolean, default: true },
    overflow: { type: Boolean, default: false }
  }

  connect () {
    enter(this.element).then(() => {
      if (this.autofocusValue && !this.element.contains(document.activeElement)) this.element.focus()
      if (this.overflowValue) document.body.classList.add('overflow-hidden')
    })
  }

  disconnect () {
    this.#dialogTurboFrame.src = null

    if (this.overflowValue) document.body.classList.remove('overflow-hidden')
  }

  hide () {
    leave(this.element).then(() => {
      this.element.remove()
      this.#dialogTurboFrame.src = null

      if (this.overflowValue) document.body.classList.remove('overflow-hidden')
    })
  }

  hideOnSubmit (event) {
    if (event.detail.success) {
      this.hide()
    }
  }

  // private

  get #dialogTurboFrame () {
    return document.querySelector(`turbo-frame[id="${this.elementIdValue}"]`)
  }
}
